import { Link, useHistory } from "react-router-dom"
import { Group, Title, Grid, Divider, Button, Text, Stack, Anchor } from "@mantine/core"
import { modals } from '@mantine/modals';
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconCheck, IconChevronRight, IconX } from "@tabler/icons-react";
import _ from "lodash";
import { useProgramRegistrationSaveMutation } from "../../api/programregistration.api";
import { createEmptyProgramRegistration, getParticipantsNames } from "../../functions/programregistration.functions";
import { useProgramRegistrationState } from "../../state/programregistration.state";
import DetailsView from "../../Components/ProgramRegistrations/DetailsView";
import ParticipantsView from "../../Components/ProgramRegistrations/ParticipantsView";
import SessionsView from "../../Components/ProgramRegistrations/SessionsView";
import ParentGuardiansView from "../../Components/ProgramRegistrations/ParentGuardiansView";
import DiscountView from "../../Components/ProgramRegistrations/DiscountView";
import PaymentView from "../../Components/ProgramRegistrations/PaymentView";
import CustomFieldsView from "../../Components/ProgramRegistrations/CustomFieldsView";
import { RoleTemplate } from "../../Components/RoleTemplate"
import { ErrorBoundary } from "../../Components/Shared/ErrorBoundary";
import ErrorBoundaryFallback from "../../Components/Shared/ErrorBoundaryFallback";
import { ProgramRegistration } from "../../models/programregistration.models";

export interface NewRegistationPageProps {
	onSave?: (registration: ProgramRegistration) => void;
	onDiscard?: () => void;
}

const NewRegistationPage = ({ onSave, onDiscard }: NewRegistationPageProps) => {
	const history = useHistory();
	// const { } = useParams<NewRegistationPageParams>();
	const { programRegistration, setProgramRegistration, isChanged, isValid, setIsLoading, reset } = useProgramRegistrationState();
	const { mutate: saveProgramRegistration, isLoading: isSaving, isError, error } = useProgramRegistrationSaveMutation();
	// const participantNames = getParticipantsNames(programRegistration).join(', ');

	const participants = programRegistration.Data.Participants;
	let participantNames = participants.length > 0 ? `${participants[0].FirstName} ${participants[0].LastName}` : ''; //getParticipantsNames(programRegistration).join(', ');
	if (participants.length > 1) {
		participantNames += ` + ${participants.length - 1} other${participants.length > 2 ? 's' : ''}`;
	}

	const handleOnSave = () => { 
		if (onSave) {
			onSave(programRegistration);
			return;
		}

		modals.openConfirmModal({
			title: 'Confirm Save',
			centered: true,
			children: <>
				<Text size="sm">
					Are you sure you want to save this registration?
				</Text>
			</>,
			labels: { confirm: 'Yes, Save Registration', cancel: 'Cancel' },
			confirmProps: { color: 'green' },
			// onCancel: () => console.log('Cancel'),
			onConfirm: () => performSave(),
		});
	}

	const performSave = () => {
		showNotification({
			id: 'programregistration-save',
			loading: true,
			title: 'Saving registration',
			message: 'Please wait while the registration is created',
			autoClose: false,
			// disallowClose: true
		});
		
		saveProgramRegistration(programRegistration!, { 
			onSuccess: (result) => {
				setProgramRegistration(createEmptyProgramRegistration(), false);
				updateNotification({
					id: 'programregistration-save',
					title: 'Success',
					message: 'Registration has been saved',
					color: 'green',
					icon: <IconCheck size={16} />,
					loading: false,
					autoClose: 3000
				});
				history.replace({ pathname: `/registrations/view/${result.data.Id}`});
			}, onError: (error) => {
				updateNotification({
					id: 'programregistration-save',
					title: 'Error',
					message: 'An error occurred while trying to save the registration',
					color: 'red',
					icon: <IconX size={16} />,
					loading: false,
					autoClose: 3000
				});
			}
		})
	}

	const handleOnDiscard = () => {
		if (onDiscard) {
			onDiscard();
			return;
		}

		history.goBack();
	}

	return <>
		<ErrorBoundary fallback={<ErrorBoundaryFallback />}>
		<RoleTemplate role="role.user.readonly" noAccess={<>You don't have access to this.</>}>
			<Group justify="space-between" wrap="nowrap">
				<Stack gap={0}>
					<Group gap={5}>
						<Anchor component={Link} to="/" fz="sm" fw="bold">Home</Anchor>
						<IconChevronRight size="14" />
						<Anchor component={Link} to="/registrations" fz="sm" fw="bold">Registrations</Anchor>
						<IconChevronRight size="14" />
						{participantNames !== "" ? <Text fz="sm" fw="bold">New Registration</Text> : <></>}						
					</Group>
					<Title order={1} mb="sm">
						{participantNames === "" ? "New Registration" : ""}
						
						{participantNames !== "" && <>
							{/* <span> for: </span> */}
							<Text inherit span style={{ lineHeight: "unset" }}>{participantNames}</Text>
						</>}
					</Title>
				</Stack>

				<Group gap="xs" mb="xs" wrap="nowrap" style={{ alignSelf: 'flex-start' }} visibleFrom="sm">
					{isChanged && <>
						<Button onClick={handleOnSave} variant="filled" disabled={isSaving || !isValid()} hidden={!isChanged} leftSection={<IconCheck />}>Save Registration</Button>
						<Button onClick={handleOnDiscard} variant="light" disabled={isSaving} hidden={!isChanged} leftSection={<IconX />}>Discard</Button>
					</>}
				</Group>
			</Group>

			<Group gap="xs" mb="xs" wrap="nowrap" style={{ alignSelf: 'flex-start' }} hiddenFrom="sm">
				{isChanged && <>
					<Button onClick={handleOnSave} variant="filled" disabled={isSaving || !isValid()} hidden={!isChanged} leftSection={<IconCheck />}>Save Registration</Button>
					<Button onClick={handleOnDiscard} variant="light" disabled={isSaving} hidden={!isChanged} leftSection={<IconX />}>Discard</Button>
				</>}
			</Group>
			{/* <Divider /> */}

			<Grid mt="xs" mb="md">
				<Grid.Col span={{ sm: 6 }}>
					<DetailsView />
					<ParentGuardiansView />
					<ParticipantsView />
				</Grid.Col>
				<Grid.Col span={{ sm: 6 }}>
					<SessionsView />
					<CustomFieldsView />
					<DiscountView />
					<PaymentView />
				</Grid.Col>
			</Grid>

		</RoleTemplate>
		<RoleTemplate role="role.admin">
			<small>
			<pre>
				{JSON.stringify(programRegistration, null, 2)}
			</pre>
			</small>
		</RoleTemplate>
		</ErrorBoundary>
	</>
}

export default NewRegistationPage